'use client';

import { useProductList } from '@/components/product-list-context';
import { event_select_item } from '@/utils/gtm';
import { storeItemListAttribution } from '@/utils/gtm/item-attribution';
import { buildListItem } from '@/utils/gtm/item-lists';
import { useLocale } from 'next-intl';
import { useCallback, useRef } from 'react';

/**
 * Returns the click handler for a product card that opens a PDP.
 *
 * Attach it to the elements that navigate — the image and the name — never to
 * the card's root, so Add To Cart, the wishlist heart and the brand link do not
 * report a product selection.
 *
 * Does nothing outside a `ProductListProvider`: the event's `item_list_id` and
 * `item_list_name` are required, and a card that belongs to no list has none.
 */
export default function useSelectItem(product, index = 0) {
  const context = useProductList();
  const locale = useLocale();

  // Read through a ref so the handler identity stays stable and never goes
  // stale — the card re-renders on cart changes while the shopper is clicking.
  const latest = useRef(null);
  latest.current = { product, index, list: context?.list, locale };

  return useCallback(() => {
    const current = latest.current;
    if (!current.list) return;

    const item = buildListItem(current);
    if (!item) return;

    // Spec §5.3.7 — remember the originating list for the next 24 hours.
    storeItemListAttribution(item);

    event_select_item(current.list, item);
  }, []);
}
